import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  useTheme,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// ── Icons ────────────────────────────────────────────────────────────────────
const ArrowLeft = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M13 8H3M7 4l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRight = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
    <path d="M8 3v7m0 0l-3-3m3 3l3-3M3 13h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ── Mock data ────────────────────────────────────────────────────────────────
const MOCK_INVOICES = [
  { id: "INV-006", date: "12 Feb 2026", plan: "Pro Seller", method: "Visa •••• 4242", amount: "£9.99", status: "paid" },
  { id: "INV-005", date: "12 Jan 2026", plan: "Pro Seller", method: "Visa •••• 4242", amount: "£9.99", status: "paid" },
  { id: "INV-004", date: "12 Dec 2025", plan: "Pro Seller", method: "Amex •••• 1008", amount: "£9.99", status: "failed" },
  { id: "INV-003", date: "12 Nov 2025", plan: "Pro Seller", method: "Visa •••• 4242", amount: "£9.99", status: "paid" },
  { id: "INV-002", date: "12 Oct 2025", plan: "Pro Seller", method: "Visa •••• 4242", amount: "£9.99", status: "paid" },
  { id: "INV-001", date: "12 Sep 2025", plan: "Pro Seller", method: "Visa •••• 4242", amount: "£9.99", status: "paid" },
];

const FILTERS = ["All", "Paid", "Failed", "2026", "2025"];
const PAGE_SIZE = 5;

// ── Status pill ──────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.6,
      px: 1.25,
      py: 0.4,
      borderRadius: "99px",
      backgroundColor: status === "paid" ? "#f0fdf4" : "#fef2f2",
      color: status === "paid" ? "#16a34a" : "#dc2626",
      fontSize: 11,
      fontWeight: 600,
    }}
  >
    <Box
      sx={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        backgroundColor: "currentColor",
        opacity: 0.8,
      }}
    />
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </Box>
);

// ── Main page ────────────────────────────────────────────────────────────────
const BillingHistory = ({ invoices = MOCK_INVOICES }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const yellow = theme.palette.scraptradeYellow?.main || "#F5C518";

  const [activeFilter, setActiveFilter] = useState("All");
  const [page, setPage] = useState(1);

  // Filter logic
  const filtered = invoices.filter((inv) => {
    if (activeFilter === "All") return true;
    if (activeFilter === "Paid") return inv.status === "paid";
    if (activeFilter === "Failed") return inv.status === "failed";
    return inv.date.includes(activeFilter); // year filter
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilter = (f) => {
    setActiveFilter(f);
    setPage(1);
  };

  // Column header style
  const thSx = {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.disabled,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    pb: 1.5,
    pr: 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: "nowrap",
  };

  // Table cell style
  const tdSx = {
    fontSize: 13.5,
    color: theme.palette.text.primary,
    py: 1.75,
    pr: 2,
    borderBottom: `1px solid ${theme.palette.grey[50]}`,
    verticalAlign: "middle",
  };

  return (
    <Box sx={{ maxWidth: 720, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>

      {/* Back */}
      <Button
        disableRipple
        onClick={() => navigate("/settings/billing")}
        startIcon={<ArrowLeft />}
        sx={{
          mb: 3,
          p: 0,
          background: "none",
          color: theme.palette.text.secondary,
          fontSize: 13,
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { background: "none", color: theme.palette.text.primary },
        }}
      >
        Back to Billing
      </Button>

      {/* Page heading */}
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: theme.palette.text.primary, mb: 0.5 }}>
        Billing History
      </Typography>
      <Typography sx={{ fontSize: 13, color: theme.palette.text.secondary, mb: 3 }}>
        A record of all your past payments and invoices.
      </Typography>

      {/* Filter chips */}
      <Box sx={{ display: "flex", gap: 1, mb: 2.5, flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <Button
            key={f}
            disableRipple
            onClick={() => handleFilter(f)}
            sx={{
              px: 1.75,
              py: 0.75,
              borderRadius: "99px",
              border: `1.5px solid ${activeFilter === f ? "#111" : theme.palette.divider}`,
              backgroundColor: activeFilter === f ? "#111" : "#fff",
              color: activeFilter === f ? "#fff" : theme.palette.text.secondary,
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              minWidth: "auto",
              "&:hover": {
                backgroundColor: activeFilter === f ? "#222" : theme.palette.grey[50],
                borderColor: activeFilter === f ? "#222" : "#ccc",
              },
            }}
          >
            {f}
          </Button>
        ))}
      </Box>

      {/* Table card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3.5 }, pt: 2.5, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Date</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Plan</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "left", display: { xs: "none", sm: "table-cell" } }}>Payment Method</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Amount</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "left" }}>Status</Box>
                <Box component="th" sx={{ ...thSx, textAlign: "right", pr: 0 }}>Invoice</Box>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <Box component="td" colSpan={6} sx={{ py: 5, textAlign: "center", color: theme.palette.text.disabled, fontSize: 13 }}>
                    No invoices found.
                  </Box>
                </tr>
              ) : (
                paginated.map((inv, i) => (
                  <tr key={inv.id}>
                    <Box component="td" sx={{ ...tdSx, fontWeight: 500, borderBottom: i === paginated.length - 1 ? "none" : tdSx.borderBottom }}>
                      {inv.date}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, borderBottom: i === paginated.length - 1 ? "none" : tdSx.borderBottom }}>
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1,
                          py: 0.3,
                          borderRadius: "5px",
                          backgroundColor: "#FFF8DC",
                          color: "#b45309",
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {inv.plan}
                      </Box>
                    </Box>
                    <Box
                      component="td"
                      sx={{
                        ...tdSx,
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                        borderBottom: i === paginated.length - 1 ? "none" : tdSx.borderBottom,
                        display: { xs: "none", sm: "table-cell" },
                      }}
                    >
                      {inv.method}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, fontWeight: 700, borderBottom: i === paginated.length - 1 ? "none" : tdSx.borderBottom }}>
                      {inv.amount}
                    </Box>
                    <Box component="td" sx={{ ...tdSx, borderBottom: i === paginated.length - 1 ? "none" : tdSx.borderBottom }}>
                      <StatusPill status={inv.status} />
                    </Box>
                    <Box component="td" sx={{ ...tdSx, textAlign: "right", pr: 0, borderBottom: i === paginated.length - 1 ? "none" : tdSx.borderBottom }}>
                      <Button
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => {}}
                        sx={{
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: "none",
                          border: `1px solid ${theme.palette.divider}`,
                          color: theme.palette.text.secondary,
                          borderRadius: "7px",
                          px: 1.5,
                          py: 0.6,
                          "&:hover": { borderColor: yellow, color: "#111" },
                        }}
                      >
                        PDF
                      </Button>
                    </Box>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>

        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: { xs: 2, md: 3.5 },
              py: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} invoices
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75 }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <Button
                  key={p}
                  disableRipple
                  onClick={() => setPage(p)}
                  sx={{
                    minWidth: 30,
                    width: 30,
                    height: 30,
                    p: 0,
                    borderRadius: "7px",
                    border: `1px solid ${p === page ? "#111" : theme.palette.divider}`,
                    backgroundColor: p === page ? "#111" : "#fff",
                    color: p === page ? "#fff" : theme.palette.text.secondary,
                    fontSize: 12,
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: p === page ? "#222" : theme.palette.grey[50],
                    },
                  }}
                >
                  {p}
                </Button>
              ))}
              {page < totalPages && (
                <Button
                  disableRipple
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  sx={{
                    minWidth: 30,
                    width: 30,
                    height: 30,
                    p: 0,
                    borderRadius: "7px",
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: "#fff",
                    color: theme.palette.text.secondary,
                    "&:hover": { backgroundColor: theme.palette.grey[50] },
                  }}
                >
                  <ArrowRight />
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BillingHistory;